import {
    SportsBasketball as AthleteIcon,
    School as CoachIcon,
    FamilyRestroom as GuardianIcon,
    Business as OrgIcon
} from "@mui/icons-material";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { Reveal } from "src/theme/animation";

export type RoleSlug = "athlete" | "guardian" | "coach" | "organization";

const ROLE_DROP_SHADOW =
    "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8)) drop-shadow(0 2px 8px rgba(0, 0, 0, 0.45))";

export type RoleOption = {
    slug: RoleSlug;
    label: string;
    icon: React.ReactNode;
    image: string;
};

export const ROLES: RoleOption[] = [
    { slug: "athlete", label: "ATHLETE", icon: <AthleteIcon sx={{ filter: ROLE_DROP_SHADOW }} />, image: "/images/athlete.png" },
    { slug: "guardian", label: "GUARDIAN", icon: <GuardianIcon sx={{ filter: ROLE_DROP_SHADOW }} />, image: "/images/guardian.png" },
    { slug: "coach", label: "COACH", icon: <CoachIcon sx={{ filter: ROLE_DROP_SHADOW }} />, image: "/images/coach.png" },
    { slug: "organization", label: "ORGANIZATION", icon: <OrgIcon sx={{ filter: ROLE_DROP_SHADOW }} />, image: "/images/organizations.png" }
];

/** FAQ role slugs → role FAQ pages. */
export const FAQ_PATH_BY_ROLE: Record<RoleSlug, string> = {
    athlete: "/faqs/athlete",
    guardian: "/faqs/guardian",
    coach: "/faqs/coach",
    organization: "/faqs/organization"
};

/** FAQ role slugs → intake survey lanes (guardian uses parent intake). */
export const INTAKE_PATH_BY_ROLE: Record<RoleSlug, string> = {
    athlete: "/intake/athlete",
    guardian: "/intake/parent",
    coach: "/intake/coach",
    organization: "/intake/organization"
};

export interface RoleSelectCardsProps {
    /** Map of role slug → destination path. */
    pathByRole: Record<RoleSlug, string>;
}

const RoleSelectCards: React.FC<RoleSelectCardsProps> = ({ pathByRole }) => {
    return (
        <Grid container spacing={3}>
            {ROLES.map((role, index) => (
                <Grid item xs={12} sm={6} md={3} key={role.slug}>
                    <Reveal variant="fadeUp" trigger="mount" delay={index * 0.1}>
                        <Box component="span" sx={{ display: "block", height: "100%" }}>
                            <Card
                                component={Link}
                                to={pathByRole[role.slug]}
                                sx={{
                                    position: "relative",
                                    height: "100%",
                                    minHeight: 220,
                                    textDecoration: "none",
                                    color: "inherit",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "hidden",
                                    backgroundColor: "transparent",
                                    backgroundImage: `url(${role.image})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center right",
                                    transition: "transform 0.2s, boxShadow 0.2s",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: 4
                                    }
                                }}
                            >
                                {/* Dark overlay for readability */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        bgcolor: "rgba(0,0,0,0.45)",
                                        "&:hover": { bgcolor: "rgba(0,0,0,0.35)" }
                                    }}
                                />
                                <CardContent
                                    sx={{
                                        position: "relative",
                                        flexGrow: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        textAlign: "center",
                                        py: 3,
                                        "&:last-child": { pb: 3 }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontSize: 64,
                                            lineHeight: 1,
                                            mb: 1.5,
                                            "& .MuiSvgIcon-root": { fontSize: "inherit" }
                                        }}
                                    >
                                        {role.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: "bold", letterSpacing: 1 }}>
                                        <Box component="span" sx={{ display: "inline-block", filter: ROLE_DROP_SHADOW }}>
                                            {role.label}
                                        </Box>
                                    </Typography>
                                </CardContent>
                            </Card>
                            </Box>
                    </Reveal>
                </Grid>
            ))}
        </Grid>
    );
};

export default RoleSelectCards;
